import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import endOfWeek from "date-fns/endOfWeek";
import startOfWeek from "date-fns/startOfWeek";
import subWeeks from "date-fns/subWeeks";
import i18next from "i18next";

import axios from "../../../utils/api-client";
import { setSupervisorRateLimitError } from "./../../../features/user/user-slice";
import { MinHoursComplianceReportState } from "./min-hours-compliance-report-types";

const initialState: MinHoursComplianceReportState = {
  reports: [],
  getReports: {
    status: "idle",
    errorMessage: "",
  },
  startDate: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }).toISOString(),
  endDate: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }).toISOString(),
};

export const getMinHoursComplianceReport = createAsyncThunk(
  "min-hours-compliance-report/get-report",
  async (
    { weekStart, weekEnd }: { weekStart: string; weekEnd: string },
    { dispatch, fulfillWithValue, rejectWithValue }
  ) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_END_POINT}/Reports/GetMinHoursComplianceReport?weekStart=${weekStart}&weekEnd=${weekEnd}`
      );
      // Backend returns 204 No Content when all workers met their minimum.
      return fulfillWithValue(res.data || []);
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      const status = error?.response?.status;
      if (status === 429) {
        dispatch(setSupervisorRateLimitError());
      }
      return rejectWithValue(errorResponse || error?.message || i18next.t("common_error-occurred"));
    }
  }
);

export const MinHoursComplianceReportSlice = createSlice({
  name: "minHoursComplianceReport",
  initialState,
  reducers: {
    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMinHoursComplianceReport.pending, (state) => {
      state.getReports.status = "pending";
    });
    builder.addCase(getMinHoursComplianceReport.fulfilled, (state, action) => {
      state.reports = action.payload;
      state.getReports.status = "fulfilled";
    });
    builder.addCase(getMinHoursComplianceReport.rejected, (state, action: any) => {
      state.getReports.status = "error";
      state.getReports.errorMessage = action.payload ?? i18next.t("common_error-occurred");
    });
  },
});

export const { setStartDate, setEndDate } = MinHoursComplianceReportSlice.actions;

export default MinHoursComplianceReportSlice.reducer;
